import { useEffect, useState } from "react";

import type { CalendarClientNoteStatusResponse } from "../@types/sharedSchema";
import type { CalendarClientNoteGate } from "../utils/calendar/helper";
import useWebotServer from "./useWebotServer";

/**
 * May the calendar form OFFER the "message to invitees" input on a meeting that has no
 * note yet (GET /api/v1/webot-calendar/client-note-status)?
 *
 * The server answers `enabled: true` only once every WhatsApp template that carries the
 * note is approved; until then a note typed here would be saved and never delivered. A
 * note that is ALREADY stored is shown whatever this says (`shouldShowClientNoteInput`).
 *
 * TRI-STATE, because "the request failed" is not "the feature is off":
 *   - `on` / `off` are answers. They are cached at module level and shared by every form.
 *   - `unknown` is no answer (still loading, the network, a 5xx, a server without the
 *     endpoint yet). It is never cached, so the next mount simply asks again.
 *
 * An answer is trusted for RECHECK_AFTER_MS. A form opened after that paints with the last
 * answer and asks again in the background; a failed re-check keeps the last answer.
 *
 * LATCHED per mount: while a form is open the value may go unknown/off → on, never
 * on → off. An input that disappears under the realtor mid-sentence is the one outcome
 * this must not produce (and what was typed is still saved: the write decision is keyed on
 * "was the input ever shown", see `resolveClientNoteWrite`).
 */

// How long an answer is trusted before the next mount asks the server again.
const RECHECK_AFTER_MS = 10 * 60 * 1000;

type GetStatus = () => Promise<CalendarClientNoteStatusResponse>;

interface CachedStatus {
  readonly gate: Exclude<CalendarClientNoteGate, "unknown">;
  readonly fetchedAt: number;
}

// Not keyed by user: the answer is about the calendar bot's templates, the same for everyone.
let cachedStatus: CachedStatus | null = null;
// One request at a time, shared by the provider's warm-up and every form that mounts meanwhile.
let inFlight: Promise<CalendarClientNoteGate> | null = null;

function lastKnownGate(): CalendarClientNoteGate {
  return cachedStatus?.gate ?? "unknown";
}

function loadStatus(getStatus: GetStatus): Promise<CalendarClientNoteGate> {
  if (cachedStatus && Date.now() - cachedStatus.fetchedAt < RECHECK_AFTER_MS) {
    return Promise.resolve(cachedStatus.gate);
  }

  if (!inFlight) {
    // Started inside the chain so that a `getStatus` that throws synchronously (no provider
    // above the caller) is a failed lookup like any other, never an error in render.
    const request: Promise<CalendarClientNoteGate> = Promise.resolve()
      .then(() => getStatus())
      .then((response) => {
        // Anything but a boolean is a malformed answer, i.e. no answer.
        if (typeof response?.enabled !== "boolean") {
          throw new Error("client-note-status answered without `enabled`");
        }
        cachedStatus = { gate: response.enabled ? "on" : "off", fetchedAt: Date.now() };
        return cachedStatus.gate;
      })
      .catch((error) => {
        console.error("Failed to load the calendar client-note status", error);
        // NOT cached, and not "off": whatever was known before still stands.
        return lastKnownGate();
      })
      .then((gate) => {
        if (inFlight === request) inFlight = null;
        return gate;
      });
    inFlight = request;
  }

  return inFlight;
}

/** `on` is kept for the life of the mount, and a failed lookup never replaces an answer. */
function latchGate(
  current: CalendarClientNoteGate,
  next: CalendarClientNoteGate
): CalendarClientNoteGate {
  if (current === "on" || next === "unknown") return current;

  return next;
}

/**
 * Ask ahead of time, so a form that opens later has its answer on the first paint instead
 * of the input popping in under the attendee cards a moment after the dialog opens.
 */
export function warmCalendarClientNoteStatus(getStatus: GetStatus): void {
  loadStatus(getStatus);
}

/** Tests only: the cache is module-level and would otherwise leak between cases. */
export function resetCalendarClientNoteStatusCacheForTests(): void {
  cachedStatus = null;
  inFlight = null;
}

export function useCalendarClientNoteEnabled(): CalendarClientNoteGate {
  const { getCalendarClientNoteStatus } = useWebotServer();

  // Seeded from the cache, stale or not, so the first paint already has the last answer.
  const [gate, setGate] = useState<CalendarClientNoteGate>(lastKnownGate);

  useEffect(() => {
    let cancelled = false;

    loadStatus(getCalendarClientNoteStatus).then((next) => {
      if (cancelled) return;
      setGate((current) => latchGate(current, next));
    });

    return () => {
      cancelled = true;
    };
    // getCalendarClientNoteStatus is re-created on every provider render; the cache means
    // a mount asks at most once, so it is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return gate;
}

export default useCalendarClientNoteEnabled;
